import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import LoginForm from './components/Auth/LoginForm';
import PasswordReset from './components/Auth/PasswordReset';
import MFAVerification from './components/Auth/MFAVerification';
import MFASetup from './components/Auth/MFASetup';
import Layout from './components/Layout/Layout';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import UserManagement from './components/Admin/UserManagement';
import GroupManagement from './components/Admin/GroupManagement/GroupManagement';
import GroupDetails from './components/Admin/GroupManagement/GroupDetails/GroupDetails';
import SystemSettings from './components/Admin/SystemSettings/SystemSettings';
import TaskManagement from './components/Admin/TaskManagement/index';
import TaskDetails from './components/Admin/TaskDetails/TaskDetails';
import GlobalLeaderboard from './components/Admin/LeaderBoard';
import BadgesAndAchievements from './components/Admin/BadgesAndAchievements';
import PointScheme from './components/Admin/PointScheme';
import CampusHierarchy from './components/Admin/CampusHierarchy';
import PermissionManagement from './components/Admin/PermissionManagement';
import PerformanceDashboard from './components/Admin/PerformanceDashboard/PerformanceDashboard';
import Profile from './components/Profile/Profile';
import NotificationsPage from './components/Notifications/NotificationsPage';
import PlatformReports from './components/PlatformReports/PlatformReports';
import CounsellingManagement from './components/Admin/Counselling/CounsellingManagement';
import FacultyEnrollmentPageComponent from './components/EnrollementForm/FacultyEnrollmentPage';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PageLoaderProvider, usePageLoaderContext } from './contexts/PageLoaderContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { WorkingModeProvider } from './contexts/WorkingModeContext';
import apiService from './services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3b82f6',
    },
    secondary: {
      main: '#10b981',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, mfaRequired } = useAuth();
  const { showLoader, hideLoader } = usePageLoaderContext();

  React.useEffect(() => {
    if (isLoading) {
      showLoader('Validating session...');
    } else {
      hideLoader();
    }
  }, [isLoading, showLoader, hideLoader]);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/signin" replace />;
  }

  if (mfaRequired) {
    return <Navigate to="/mfa/verify" replace />;
  }

  return <>{children}</>;
};

const topLevelDesignations = [
  'chairman',
  'vice_chairman',
  'director',
  'principal',
  'vice_principal',
  'dean'
];

const hasAccess = (user: any, accessType: 'all' | 'hod' | 'remaining'): boolean => {
  if (!user?.designation) {
    return accessType === 'remaining';
  }

  const designation = user.designation.toLowerCase();

  if (topLevelDesignations.includes(designation)) {
    return accessType === 'all' || accessType === 'hod' || accessType === 'remaining';
  }

  if (designation === 'head_of_department') {
    return accessType === 'hod' || accessType === 'remaining';
  }

  return accessType === 'remaining';
};

const DesignationRoute: React.FC<{
  children: React.ReactNode;
  accessType: 'all' | 'hod' | 'remaining';
  module?: string;
}> = ({ children, accessType, module }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const [isReady, setIsReady] = useState(false);
  const [userPermissions, setUserPermissions] = useState<any[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [isMainAdmin, setIsMainAdmin] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!module || !user || !isAuthenticated) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as any[];
          setUserPermissions(permissions);
          setIsMainAdmin(responseData.is_main_admin || response.is_main_admin || false);
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        setUserPermissions([]);
        setIsMainAdmin(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadPermissions();
  }, [module, user, isAuthenticated]);
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isLoading && !isAuthenticated) {
      setIsReady(true);
    }
  }, [isLoading, isAuthenticated, user]);

  useEffect(() => {
    if (isLoading || (isAuthenticated && !user) || !isReady) {
      showLoader('Loading dashboard...');
    } else {
      hideLoader();
    }
  }, [isLoading, isAuthenticated, user, isReady, showLoader, hideLoader]);

  const hasModulePermission = (moduleName: string): boolean => {
    if (permissionsLoading) {
      return false;
    }
    return userPermissions.some(perm => perm.module === moduleName);
  };
  if (isLoading || (isAuthenticated && !user) || !isReady || permissionsLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isMainAdmin || (user as any).is_main_admin) {
    return <>{children}</>;
  }

  const hasDesignationAccess = hasAccess(user, accessType);
  const hasPermissionAccess = module ? hasModulePermission(module) : false;
  if (module) {
    if (!hasPermissionAccess && !hasDesignationAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  } else {
    if (!hasDesignationAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Dashboard Router Component
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();

  // Top level designations and HOD see admin dashboard, others see member dashboard
  if (user?.designation) {
    const designation = user.designation.toLowerCase();
    if (topLevelDesignations.includes(designation) || designation === 'head_of_department') {
      return <AdminDashboard />;
    }
  }

  return <AdminDashboard />;
};

// Admin Only Route Component
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <>{children}</>;
  }

  return <Navigate to="/dashboard" replace />;
};

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, mfaRequired, user } = useAuth();
  const { showLoader, hideLoader } = usePageLoaderContext();

  // Show page loader during initial authentication check
  React.useEffect(() => {
    if (isLoading) {
      showLoader('Initializing application...');
    } else {
      hideLoader();
    }
  }, [isLoading, showLoader, hideLoader]);

  if (isLoading) {
    return null; // PageLoader will handle the visual feedback
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/auth/signin"
          element={<LoginForm />}
        />
        <Route
          path="/resetpassword"
          element={<PasswordReset />}
        />
        <Route
          path="/mfa/verify"
          element={
            mfaRequired ? <MFAVerification /> : <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="/mfa/setup"
          element={<MFASetup />}
        />
        <Route
          path="/faculty-enrollment"
          element={<FacultyEnrollmentPageComponent />}
        />

        {/* Default redirect for root path */}
        <Route
          path="/"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* Protected Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining">
                  <DashboardRouter />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes - Accessible by Top Level and HOD with permissions */}
        <Route
          path="/user-management"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="user_management">
                  <UserManagement />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management/users/:userId/performance"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="user_management">
                  <PerformanceDashboard
                    excludeTaskManagement={false}
                    hideBreadcrumb={false}
                  />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/point-scheme"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="point_scheme">
                  <PointScheme />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campus-administration"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="campus_hierarchy">
                  <CampusHierarchy />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/permission-management"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="all">
                  <PermissionManagement />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Group Management - Accessible by Top Level and HOD */}
        <Route
          path="/group-management/groups"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupManagement />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/overview"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/points-configuration"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/task-management"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/user-management"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/leader-board"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <GroupDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/tasks/:taskId"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/tasks/:taskId/submissions"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/users/:userId/performance"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <PerformanceDashboard />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management/groups/:groupId/tasks/:taskId/updates"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="hod" module="group_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Routes accessible by all designations */}
        <Route
          path="/task-management"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="task_management">
                  <TaskManagement />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task-management/:taskId"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="task_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task-management/:taskId/submissions"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="task_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task-management/:taskId/instances"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="task_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task-management/:taskId/updates"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="task_management">
                  <TaskDetails />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/badges"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="badge_achievements">
                  <BadgesAndAchievements />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining" module="notifications">
                  <NotificationsPage />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-reports"
          element={
            <ProtectedRoute>
              <Layout>
                <AdminOnlyRoute>
                  <PlatformReports />
                </AdminOnlyRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leader-board"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining">
                  <GlobalLeaderboard />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/counselling"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining">
                  <CounsellingManagement />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-settings"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining">
                  <SystemSettings />
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Reports - Only top level designations */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="all">
                  <div>Platform Reports - Coming Soon</div>
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-activities"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignationRoute accessType="remaining">
                  <div>My Activities - Coming Soon</div>
                </DesignationRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

// Main App with Auth Provider
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <PageLoaderProvider>
          <ConfirmProvider>
            <NotificationProvider>
              <WorkingModeProvider>
                <AppContent />
              </WorkingModeProvider>
            </NotificationProvider>
          </ConfirmProvider>
        </PageLoaderProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;